import {
  Link,
  Trans,
  Translation,
  useI18next,
  useTranslation,
} from "gatsby-plugin-react-i18next";
import * as React from "react";
import { useI18nLink } from "../../hooks/useI18nLink";
import { useConfig } from "../../hooks/useConfig";
import { usePushGtmEvent } from "../../hooks/useGmtPushEvent";
import Button from "../form/Button";
import FileInput from "../form/FileInput";
import { Input, InputLayout } from "../form/Input";

const initialFormData = {
  firstName: "",
  lastName: "",
  email: "",
  message: "",
  curriculum: null,
};

interface ApplyProps {
  jobPosition: object;
  classNames?: string;
}

const Apply: React.FunctionComponent<ApplyProps> = ({
  jobPosition,
  classNames = "",
}) => {
  const [formData, updateFormData] = React.useState(initialFormData);
  const [sending, setSending] = React.useState(false);
  const [showError, setShowError] = React.useState(false);
  const { jobApplication } = useConfig();
  const { t } = useTranslation();
  const pushGtmEvent = usePushGtmEvent();
  const { navigate } = useI18next();
  const i18nLink = useI18nLink();

  const onFormSubmit = async (event) => {
    event.preventDefault();
    setSending(true);
    setShowError(false);

    try {
      await fetch(jobApplication.endpoint, {
        method: "POST",
        body: JSON.stringify({
          ...formData,
          curriculum: await fileToBase64(formData.curriculum),
          jobPosition,
        }),
      });
      navigate("/join/thanks");
      pushGtmEvent({ event: "apply_form_submit" });
    } catch (e) {
      setShowError(true);
      setSending(false);
    }
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handleFileUpload = async (file) =>
    updateFormData({
      ...formData,
      curriculum: file,
    });

  const handleFormChange = (event) =>
    updateFormData({
      ...formData,
      [event.target.name]: event.target.value.trim(),
    });

  return (
    <form onSubmit={onFormSubmit} className={classNames}>
      <div className="flex flex-wrap">
        <Input
          id="firstName"
          type="text"
          placeholder={t("join:joinForm.firstNamePlaceholder")}
          label={t("join:joinForm.firstName")}
          name="firstName"
          mandatory
          handleFormChange={handleFormChange}
          sending={sending}
          className="w-1/2 pr-2"
        />
        <Input
          id="lastName"
          type="text"
          placeholder={t("join:joinForm.lastNamePlaceholder")}
          label={t("join:joinForm.lastName")}
          name="lastName"
          mandatory
          handleFormChange={handleFormChange}
          sending={sending}
          className="w-1/2 pl-2"
        />
        <Input
          id="email"
          type="email"
          placeholder={t("join:joinForm.emailPlaceholder")}
          label={t("join:joinForm.email")}
          name="email"
          mandatory
          handleFormChange={handleFormChange}
          sending={sending}
          className="w-full"
        />
        <Input
          id="message"
          type="textarea"
          placeholder={t("join:joinForm.messagePlaceholder")}
          label={t("join:joinForm.message")}
          name="message"
          mandatory
          handleFormChange={handleFormChange}
          sending={sending}
          className="w-full"
        />
        <InputLayout
          label={t("join:joinForm.curriculum")}
          mandatory
          name="curriculum"
          className="w-full"
        >
          <FileInput
            placeholder={t("join:joinForm.curriculumPlaceholder")}
            onChange={handleFileUpload}
            disabled={sending}
            accept="application/pdf"
          />
        </InputLayout>
        <div className="py-2 w-full">
          <div className="flex space-x-2 items-center mt-2">
            <input
              type="checkbox"
              name="privacy1"
              required
              className="cursor-pointer h-6 w-6 shadow-sm rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-base outline-none text-indigo-500 py-1 px-3 leading-8 transition-colors duration-200 ease-in-out"
            />
            <label className="text-sm font-bold text-gray-500 dark:text-gray-400">
              {t("join:joinForm.privacy.check3")}
            </label>
          </div>
          <div className="flex space-x-2  items-center mt-2">
            <input
              type="checkbox"
              name="privacy1"
              required
              className="cursor-pointer h-6 w-6 shadow-sm rounded-lg border border-gray-300 dark:border-gray-700 dark:bg-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 text-base outline-none text-indigo-500 py-1 px-3 leading-8 transition-colors duration-200 ease-in-out"
            />
            <label className="text-xs text-gray-500 dark:text-gray-400">
              <Trans
                i18nKey="join:joinForm.privacy.check1"
                components={{
                  policy: (
                    <a
                      href={i18nLink("/privacy/privacy-policy")}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline semibold"
                    />
                  ),
                }}
              />
            </label>
          </div>
        </div>
        <div className="pt-6 w-full">
          {showError && (
            <div className="w-full mb-4 py-3 px-5 bg-red-50 rounded-xl text-red-700 dark:bg-red-900/30 dark:text-red-300">
              <span className="font-bold">
                {t("join:joinForm.error.title")}
              </span>{" "}
              {t("join:joinForm.error.message")}
            </div>
          )}
          <Button
            type="submit"
            variant="accent"
            loading={sending}
            disabled={sending}
            text={t("join:joinForm.send")}
            loadingText={t("join:joinForm.sending")}
          />
        </div>
      </div>
    </form>
  );
};

export default Apply;
